/**
 * EASY_CONTRACTOR_LandingPage_v01_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01
 * Date: 2025-09-05 (America/Denver)
 *
 * Purpose:
 *  - Simple, high-conversion landing page for EasyContractorInsurance.com
 *  - Capture core contact + business details and the contractor type (dropdown)
 *  - Wizard + Vertical modes reuse the exact same UI sections
 *  - Every submitted value is sent as a STRING (wire payload is Record<string,string>)
 *
 * Notes:
 *  - Minimal validation: presence + lightweight email check
 *  - Replace API_URL with your endpoint; TEST_MODE true will skip the network
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://example.com/api/lead"; // <— replace with your API Gateway endpoint
const AGENT_PHONE_NUMBER = "(303) 337-4433";

/* ============================================================
   TYPES
============================================================ */
/** Raw lead payload. NOTE: everything that can be text is a string. */
interface ILeadPayload {
  // Contact & Business
  fullName: string;
  email: string;
  phone: string;

  businessName: string;
  yearsInBusiness: string;   // stringified
  employeesCount: string;    // stringified
  website?: string;

  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  // Work details
  contractorType: string;
  serviceRadiusMiles?: string; // optional
  licensedBonded?: string;     // "yes" | "no" | ""

  // Notes
  notes?: string;

  // Meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;

  utm?: { source?: string; medium?: string; campaign?: string };
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS / CONSTANTS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

// Drop-down options provided by the user (plus Electrical + Other at the end).
const CONTRACTOR_TYPES: string[] = [
  "Commercial Building Contractor",
  "Residential Building Contractor",
  "Roofing Contractor",
  "Swimming Pool Contractor",
  "Fire Alarm Systems Contractor",
  "Fire Spinkler Systems Contractor",
  "Fire Extinguishing Systems Contractor",
  "Fuel Tank Contractor",
  "Mobile Home Installer",
  "Demolition Contractor",
  "Moving Contractor",
  "Sign Contractor",
  "Mechanical Systems Contractor",
  "Residential Elevator Contractor",
  "Plumbing Contractor",
  "Right Of Way Contractor",
  "Limited Building Commercial Contractor",
  "Remodelng Contractor",
  "Underground Fire Service Line Contractor",
  "Emergency Responder Radio Coverage Contractor",
  "Electrical Contractor",
  "Other",
];

const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
const dash = (v?: string | number | boolean | null) => {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s.length ? s : "—";
};

/* ============================================================
   MAIN
============================================================ */
export const EASY_CONTRACTOR_LandingPage_v01_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);

  // Contact & Business (strings)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [yearsInBusiness, setYearsInBusiness] = useState<string>("0");
  const [employeesCount, setEmployeesCount] = useState<string>("0");
  const [website, setWebsite] = useState<string>("");

  // Address (strings)
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Work details
  const [contractorType, setContractorType] = useState<string>("");
  const [serviceRadiusMiles, setServiceRadiusMiles] = useState<string>("");
  const [licensedBonded, setLicensedBonded] = useState<string>("");

  // Notes / submit
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Work Details", "Notes & Submit", "Confirmation"] as const;

  /* ---------- Minimal validation ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: phone.trim().length >= 7,
    businessName: businessName.trim().length >= 2,

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: zipCode.trim().length >= 3,

    contractorType: contractorType !== "",
  };

  const valid = useMemo(() => {
    return (
      V.fullName && V.email && V.phone &&
      V.businessName &&
      V.address1 && V.city && V.state && V.zip &&
      V.contractorType
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.businessName && V.address1 && V.city && V.state && V.zip;
      case 1:
        return V.contractorType;
      case 2:
        return true; // notes optional
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","businessName","address1","city","state","zip"]);
    else if (currentStep === 1) touchMany(["contractorType"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  /* ============================================================
     PAYLOAD & SUBMIT (EVERYTHING => strings)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    return {
      // Contact & Business
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),

      businessName: businessName.trim(),
      yearsInBusiness: yearsInBusiness.trim(),
      employeesCount: employeesCount.trim(),
      website: website?.trim() || "",

      // Address
      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      // Work details
      contractorType: contractorType.trim(),
      serviceRadiusMiles: serviceRadiusMiles?.trim() || "",
      licensedBonded: licensedBonded || "",

      // Notes
      notes: notes?.trim() || "",

      // Meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.contractor.quote",
      itemClass: "quote.request",
      itemType: "insurance.contractor.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (Array.isArray(v)) {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else {
        out[k] = v == null ? "" : String(v);
      }
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Mark core fields
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      businessName: true, address1: true, city: true, state: true, zip: true,
      contractorType: true,
    }));

    if (!valid) {
      setSubmitError("Please complete required fields marked in red.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 400));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Easy <span className="text-primary">Contractor</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033374433">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Main Card */}
      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <Card className="shadow-lg">
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Contractor Quote</div>
                      <small className="text-body-secondary">Tell us about your business so we can match the right policy.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>

                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Business">
                                <SectionContactBusiness
                                  values={{ fullName, email, phone, businessName, yearsInBusiness, employeesCount, website, address1, address2, city, state, zipCode }}
                                  setters={{ setFullName, setEmail, setPhone, setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                  V={V} vp={vp} vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Work Details">
                                <SectionWorkDetails
                                  values={{ contractorType, serviceRadiusMiles, licensedBonded }}
                                  setters={{ setContractorType, setServiceRadiusMiles, setLicensedBonded }}
                                  V={V} vp={vp} vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <Col xs={12}>
                              <SectionCard title="Notes">
                                <SectionNotes values={{ notes }} setters={{ setNotes }} vpOpt={vpOpt} />
                                <div className="d-flex justify-content-end mt-3">
                                  <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                    {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                                  </Button>
                                </div>
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button type="button" variant="outline-secondary" onClick={(e) => { e.preventDefault(); e.stopPropagation(); goBack(); }} disabled={currentStep === 0}>
                              Previous
                            </Button>
                            {currentStep < 2 ? (
                              <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }} disabled={!isStepValid(currentStep)}>
                                Next
                              </Button>
                            ) : null}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Business">
                                  <SectionContactBusiness
                                    values={{ fullName, email, phone, businessName, yearsInBusiness, employeesCount, website, address1, address2, city, state, zipCode }}
                                    setters={{ setFullName, setEmail, setPhone, setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                    V={V} vp={vp} vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Work Details">
                                  <SectionWorkDetails
                                    values={{ contractorType, serviceRadiusMiles, licensedBonded }}
                                    setters={{ setContractorType, setServiceRadiusMiles, setLicensedBonded }}
                                    V={V} vp={vp} vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Notes">
                                  <SectionNotes values={{ notes }} setters={{ setNotes }} vpOpt={vpOpt} />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about contractor insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Easy Contractor Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Business ---------- */
const SectionContactBusiness: React.FC<{
  values: {
    fullName: string; email: string; phone: string;
    businessName: string; yearsInBusiness: string; employeesCount: string; website: string;
    address1: string; address2: string; city: string; state: string; zipCode: string;
  };
  setters: {
    setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void;
    setBusinessName: (v: string) => void; setYearsInBusiness: (v: string) => void; setEmployeesCount: (v: string) => void; setWebsite: (v: string) => void;
    setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { fullName, email, phone, businessName, yearsInBusiness, employeesCount, website, address1, address2, city, state, zipCode } = values;
  const { setFullName, setEmail, setPhone, setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite, setAddress1, setAddress2, setCity, setState, setZipCode } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Contact - Full Name</Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="businessName">Business Name</Form.Label>
        <Form.Control id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Acme Builders LLC" required {...vp("businessName", V.businessName)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="website">Website (optional)</Form.Label>
        <Form.Control id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" {...vpOpt("website", !!website.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Please enter a phone number.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="yib">Years in Business</Form.Label>
        <Form.Control id="yib" inputMode="numeric" value={yearsInBusiness} onChange={(e) => setYearsInBusiness(e.target.value)} placeholder="0" />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="emp">Employees</Form.Label>
        <Form.Control id="emp" inputMode="numeric" value={employeesCount} onChange={(e) => setEmployeesCount(e.target.value)} placeholder="0" />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Suite</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Please enter your zip.</Form.Control.Feedback>
      </Col>
    </Row>
  );
};

/* ---------- Work Details ---------- */
const SectionWorkDetails: React.FC<{
  values: { contractorType: string; serviceRadiusMiles: string; licensedBonded: string; };
  setters: { setContractorType: (v: string) => void; setServiceRadiusMiles: (v: string) => void; setLicensedBonded: (v: string) => void; };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { contractorType, serviceRadiusMiles, licensedBonded } = values;
  const { setContractorType, setServiceRadiusMiles, setLicensedBonded } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="contractorType">Type of Contractor</Form.Label>
        <Form.Select id="contractorType" value={contractorType} onChange={(e) => setContractorType(e.target.value)} required {...vp("contractorType", V.contractorType)}>
          <option value="">Select</option>
          {CONTRACTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </Form.Select>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="radius">Service Radius (miles, optional)</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>mi</InputGroup.Text>
          <Form.Control id="radius" inputMode="numeric" value={serviceRadiusMiles} onChange={(e) => setServiceRadiusMiles(e.target.value)} placeholder="e.g., 50" {...vpOpt("serviceRadiusMiles", !!serviceRadiusMiles)} />
        </InputGroup>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="licensedBonded">Licensed / Bonded?</Form.Label>
        <Form.Select id="licensedBonded" value={licensedBonded} onChange={(e) => setLicensedBonded(e.target.value)} {...vpOpt("licensedBonded", !!licensedBonded)}>
          <option value="">Select (optional)</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </Form.Select>
      </Col>
    </Row>
  );
};

/* ---------- Notes ---------- */
const SectionNotes: React.FC<{
  values: { notes: string };
  setters: { setNotes: (v: string) => void };
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, vpOpt }) => {
  const { notes } = values;
  const { setNotes } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="notes">Additional Notes (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Projects, certifications, timelines, special hazards, etc." {...vpOpt("notes", !!notes.trim())} />
      </Col>
    </Row>
  );
};

/* ============================================================
   Confirmation block (Sectioned, readable, no JSON)
============================================================ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! An agent will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        <div className="mt-3">
          <Section title="Contact & Business">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={dash(app.phone)} />
            <KV label="Business" value={dash(app.businessName)} />
            <KV label="Years in Business" value={dash(app.yearsInBusiness)} />
            <KV label="Employees" value={dash(app.employeesCount)} />
            <KV label="Website" value={dash(app.website)} />
          </Section>

          <Section title="Address">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Suite" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Work Details">
            <KV label="Contractor Type" value={dash(app.contractorType)} />
            <KV label="Service Radius" value={app.serviceRadiusMiles ? app.serviceRadiusMiles + " miles" : "—"} />
            <KV label="Licensed/Bonded" value={dash(app.licensedBonded)} />
          </Section>

          <Section title="Notes">
            <div className="p-3 bg-body-tertiary border rounded mb-2">{dash(app.notes)}</div>
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default EASY_CONTRACTOR_LandingPage_v01_WIZARD;
